/* eslint-disable react/prop-types, react/display-name */

import { memo, useContext, useState, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import CreatableSelect from 'react-select/creatable'

import { components } from 'react-select'

import { encodeText } from '@aytm/app/stats/lib/util/helpers'
import saveFile from '@aytm/app/stats/page/comments/lib/download/save-file'

import { CODEBOOKS_COLORS, TAG_MAX_LENGTH, TAG_MIN_LENGTH } from '@aytm/app/stats/page/components/lib/constants'
import { HIDDEN_TAG_TYPES } from '@aytm/app/stats/page/comments/lib/constants'
import { DEFAULT_CODEBOOK, FORBIDDEN_TAG_SYMBOLS } from '@aytm/app/stats/page/sidebar/coding//lib/constants'

import { OEContext } from '@aytm/app/stats/page/comments'

import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import style from '@aytm/app/stats/page/comments/comments/styles/ToolBox.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

const { MultiValue, Option } = components

const OPTION_REG = new RegExp(`^[^${FORBIDDEN_TAG_SYMBOLS}]*$`, 'i')

const rsStyles = {
  option: (provided, state) => {
    return { ...provided, isDisabled: 'codebook' == state.data?.type }
  },
  multiValue: (provided, state) => {
    return { ...provided, backgroundColor: state.styles.backgroundColor }
  }
}

const rsComponents = {
  DropdownIndicator: null,
  MultiValue: ({ children, data, ...props }) => {
    const backgroundColor =
      'batch' != data.type && data.color
        ? CODEBOOKS_COLORS.some(color => color[0].toLowerCase() == data.color.toLowerCase())
          ? CODEBOOKS_COLORS.find(color => color[0].toLowerCase() == data.color.toLowerCase())[1].toLowerCase()
          : `${data.color}90`
        : '#efefef'

    return (
      <MultiValue data={data} styles={{ backgroundColor }} {...props}>
        <span title={`<b>${data.codebook}</b> codebook`} dangerouslySetInnerHTML={{ __html: children }} />
      </MultiValue>
    )
  },
  Option: ({ children, data, ...props }) => (
    <Option data={data} {...props}>
      <span
        className={classNames(style['comment-tools-tags-value'], {
          [style['comment-tools-tags-sub-value']]: Object.hasOwn(data, 'codebook')
        })}
      >
        <span dangerouslySetInnerHTML={{ __html: children }} />
      </span>
    </Option>
  )
}

const rsFormatGroupLabel = data => (
  <span className={style['comment-tools-tags-group']} dangerouslySetInnerHTML={{ __html: data.label }}></span>
)

const rsFormatCreateLabel = value => `Create: ${encodeText(value, false)}`

const ToolBox = memo(
  ({ tags, path, dataId, responseId, downloadUrl, toolBoxOptions, isDisabledToolBox, onAddTag, onRemoveTag }) => {
    const { searchState, commentStatusesState } = useContext(OEContext)

    const { tagsList, isTagsManagementDisabled } = searchState

    const tagsStatus = commentStatusesState.tags[responseId] || {}

    const [isDisabled, setIsDisabled] = useState(isDisabledToolBox || false)

    const downloadName = `AYTM-Survey-${PageGlobals.SURVEY_ID}-Video-Response-${responseId}_${path}.mp4`

    const onDownloadClick = event => {
      event.preventDefault()
      saveFile(downloadUrl, downloadName)
    }

    const inputValue = useMemo(
      () =>
        tags
          .filter(tagItem => !['junk', 'sentiment', ...HIDDEN_TAG_TYPES].includes(tagItem.type))
          .map(tagItem => tagsList.find(tag => tag.tag == tagItem.tag))
          .filter(tagItem => tagItem != undefined && null !== tagItem),
      [tags, tagsList]
    )

    const filterOption = useCallback(
      (option, searchText) => {
        const text = searchText.trim().toLowerCase()

        if (0 < text.length) {
          return (
            option.data.__isNew__ ||
            option.data.label.toLowerCase().includes(encodeText(text, false)) ||
            ('codebook' == option.data.type &&
              option.data.tags.some(tag => tag.toLowerCase().includes(encodeText(text, false))))
          )
        } else {
          const { label, value } = option
          return !inputValue.some(
            v =>
              v.label == label &&
              ('tag' == v.type
                ? (null !== v.codebook ? `${v.codebook}: ${v.value}` : v.value) == value
                : v.value == value)
          )
        }
      },
      [inputValue]
    )

    const isValidNewOption = useCallback(
      value => {
        const _value = value.trim()
        return (
          OPTION_REG.test(_value) &&
          _value.length >= TAG_MIN_LENGTH &&
          _value.length <= TAG_MAX_LENGTH &&
          !toolBoxOptions.some(group =>
            group.options.some(
              option =>
                _value.toLowerCase() == option.label.toLowerCase() &&
                DEFAULT_CODEBOOK.toLowerCase() == option.codebook.toLowerCase()
            )
          )
        )
      },
      [toolBoxOptions]
    )

    const onChange = async (newValue, actionMeta) => {
      switch (actionMeta.action) {
        case 'pop-value':
        case 'remove-value': {
          if (!Object.hasOwn(actionMeta, 'removedValue')) return

          const tag = {
            tag: actionMeta.removedValue.value,
            type: actionMeta.removedValue.type,
            label: actionMeta.removedValue.label,
            codebook: actionMeta.removedValue.codebook
          }

          setIsDisabled(true)
          await onRemoveTag(tag, path, responseId)
          setIsDisabled(false)

          break
        }
        case 'select-option':
        case 'create-option':
        default: {
          if (!Object.hasOwn(actionMeta, 'option') && !isValidNewOption(newValue[newValue.length - 1].value)) return

          const tag = Object.hasOwn(actionMeta, 'option')
            ? {
                tag: actionMeta.option.value,
                type: actionMeta.option.type,
                label: actionMeta.option.label,
                codebook: actionMeta.option.codebook
              }
            : {
                tag: encodeText(newValue[newValue.length - 1].value, false).trim(),
                type: 'manual',
                label: encodeText(newValue[newValue.length - 1].label, false).trim(),
                codebook: null
              }

          if (onAddTag && 'function' == typeof onAddTag) {
            setIsDisabled(true)
            await onAddTag(tag, path, responseId, 'create')
            setIsDisabled(false)
          }

          break
        }
      }
    }

    useEffect(() => {
      setIsDisabled(isDisabledToolBox)
    }, [isDisabledToolBox])

    return (
      <div id={dataId} className={style['comment-tools']}>
        <div className={style['controls-block']}>
          {null !== downloadUrl ? (
            <a
              className={`${style['download-button']} p-button p-button-rounded p-button-blue`}
              href={downloadUrl}
              download={downloadName}
              target="_blank"
              rel="noreferrer"
              onClick={onDownloadClick}
            >
              DOWNLOAD
            </a>
          ) : null}
        </div>
        {tagsStatus[path] ? (
          <Popup
            width="215px"
            direction="topleft"
            popupOffset={15}
            portal
            show={'error' == tagsStatus[path].type}
            hideOtherPopupsOnShow
            content={() => <div className={style['response-header-notification-text']}>{tagsStatus[path].message}</div>}
          />
        ) : null}
        <CreatableSelect
          key={`${dataId}-tagItem`}
          className="comment-tools-tags-input"
          classNamePrefix="comment-tools-tags-input"
          components={rsComponents}
          styles={rsStyles}
          placeholder={!isTagsManagementDisabled ? '+ add code' : ''}
          options={toolBoxOptions}
          value={inputValue}
          createOptionPosition="first"
          cacheOptions
          isMulti
          isSearchable
          isClearable={false}
          isLoading={isDisabled}
          isDisabled={isDisabled || isTagsManagementDisabled}
          isValidNewOption={isValidNewOption}
          onChange={onChange}
          filterOption={filterOption}
          formatGroupLabel={rsFormatGroupLabel}
          formatCreateLabel={rsFormatCreateLabel}
        />
      </div>
    )
  }
)

ToolBox.propTypes = {
  tags: PropTypes.array,
  path: PropTypes.string,
  dataId: PropTypes.string,
  responseId: PropTypes.number,
  downloadUrl: PropTypes.string,
  toolBoxOptions: PropTypes.array,
  isDisabledToolBox: PropTypes.bool,
  onAddTag: PropTypes.func,
  onRemoveTag: PropTypes.func
}

ToolBox.displayName = 'ToolBox'

export default ToolBox
